import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Oval as Loader } from "react-loader-spinner";
import { fetchPurchasedContent } from "../../../../../shared/actions/contentAction";

import "../home.css";
import FeedPost from "../../../../../features/cards/user/feed";
import NoContent from "../../../../component/NoContent/NoContent";
import { ROLE } from "../../../../../shared/constants/common";
import { reportError } from "../../../../../shared/sentry";

const PurchasedTab = ({ 
  userInfo, 
  fetchPurchasedContent, 
  purchasedContent, 
  purchasedContentHasMore, 
  purchasedContentPage 
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [limit] = useState(6);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [processedPurchasedData, setProcessedPurchasedData] = useState([]);
  const loadMoreSentinelRef = useRef(null);

  const processFeedData = useCallback((data) => {
    const feedData = data || [];
    
    if (!userInfo) {
      return feedData;
    }

    // return feedData;
    return feedData.map(item => {
      // Handle purchasedContent structure where content and profile are nested
      const nestedProfile = {
        ...(item.content?.profile || {}),
        ...(item.profile || {}),
      };
      const contentItem = item.content
        ? { ...item.content, profile: nestedProfile }
        : { ...item, profile: nestedProfile };

      // in this api we get only purchased content, so we need to add isPurchasedOrSubscribed to true
      return {
        ...contentItem,
        isPurchasedOrSubscribed: true,
        profile: {
          ...nestedProfile,
          userid: nestedProfile.userid || nestedProfile._id,
        },
      };
    });
  }, [userInfo]);

  const getPurchasedContent = useCallback(async (page = 0, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetchPurchasedContent(limit, page, isLoadMore);
      
      if (response.success) {
        setHasInitiallyLoaded(true);
      }
    } catch (error) {
      reportError(error, 'PurchasedTab fetch purchased content');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [limit, fetchPurchasedContent]);

  useEffect(() => {
    if (purchasedContent?.length > 0 && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
      setLoading(false);
    } else if (!hasInitiallyLoaded) {
      getPurchasedContent(0, false);
    }
  }, [purchasedContent, hasInitiallyLoaded, getPurchasedContent]);

  useEffect(() => {
    setProcessedPurchasedData(processFeedData(purchasedContent || []));
  }, [purchasedContent, processFeedData]);

  const loadMore = useCallback(() => {
    // Check if there's more data available and not currently loading
    if (!purchasedContentHasMore || loadingMore || loading) {
      return;
    }

    getPurchasedContent(purchasedContentPage + 1, true);
  }, [purchasedContentHasMore, loadingMore, loading, purchasedContentPage, getPurchasedContent]);

  // Setup Intersection Observer for load more (more reliable across browsers)
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    // Use Intersection Observer API for better cross-browser compatibility
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // When sentinel element is visible (or near visible), load more
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Trigger 200px before reaching the sentinel
        threshold: 0.1 // Trigger when 10% of sentinel is visible
      }
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [loadMore]);

  const handleDropdownToggle = useCallback((postId) => {
    setOpenDropdownId(prev => prev === postId ? null : postId);
  }, []);

  if (loading && !hasInitiallyLoaded) {
    return (
      <div className="modern-feed-loading modern_feed_loading">
        <div>
          <Loader color="#F334BE" height={30} width={30} />
          <h5>Loading purchased content...</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-feed">
      <div className="feed-container">
        {processedPurchasedData.length > 0 && processedPurchasedData.map((item, index) => (
          <div key={item._id} data-content-id={item._id}>
            <FeedPost
              postId={item?.content?._id || index}
              post={item}
              creatorRole={item?.profile?.rol || null}
              isCreatorOwnProfile={userInfo?._id === item?.profile?._id}
              creatorInfo={item?.profile || {}}
              openDropdownId={openDropdownId}
              onDropdownToggle={handleDropdownToggle}
              isShowDuration={true}
              showControls={true}
              isPPVContent={item?.profile?.rol === ROLE.CREATOR_CONTENT}
              isSubContent={item?.profile?.rol === ROLE.CREATOR_SUB}
              isShowChatButton={true}
              isShowEllipsis={false}
              showPin={false}
            />
          </div>
        ))}
        
        {!loading && hasInitiallyLoaded && processedPurchasedData.length === 0 && (
          <NoContent 
            isLoading={loading}
            hasData={processedPurchasedData.length > 0}
            message="Nothing here yet!"
          />
        )}

        {/* Sentinel element for Intersection Observer - triggers load more when visible */}
        {hasInitiallyLoaded && purchasedContentHasMore && !loadingMore && (
          <div ref={loadMoreSentinelRef} style={{ height: '1px', width: '100%' }} />
        )}

        {loadingMore && purchasedContentHasMore && (
          <div className="loading-more">
            <div className="loading-more-spinner">
              <Loader type="Oval" color="#F334BE" height={40} width={40} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.user.user,
  purchasedContent: state.content?.purchasedContent || [],
  purchasedContentHasMore: state.content?.purchasedContentHasMore ?? true,
  purchasedContentPage: state.content?.purchasedContentPage || 0,
});

const mapDispatchToProps = {
  fetchPurchasedContent,
};

export default connect(mapStateToProps, mapDispatchToProps)(PurchasedTab);